import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { supabase } from '@/integrations/supabase/client'
import { ChevronUp, ChevronDown, ChevronsUpDown, Clock, UsersRound, ClipboardList } from 'lucide-react'
import { displayTag, tagActiveClass, computeNewTags, TagIcon } from '@/components/ui/TagBadges'
import { PageHeader } from '@/components/ui/PageHeader'
import { useState } from 'react'
import { useAppData } from '@/contexts/AppDataContext'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'

interface DerniersContactsProps {
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
    onNavigateToBeneficiaire?: (id: string) => void
    embedded?: boolean
}

interface TenantContact {
    id: string
    name: string
    numero_suivi: number | null
    lastFaceToFace: string | null
    lastPhoneCall: string | null
    lastNote: string | null
    tags: string[]
}

export const DerniersContacts = ({
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm,
    onNavigateToBeneficiaire,
    embedded = false,
}: DerniersContactsProps) => {
    const { summaries, loading, refresh, updateSummaryTags } = useAppData()

    type SortCol = 'name' | 'lastContact' | 'lastFaceToFace' | 'lastPhoneCall' | 'lastNote'
    const [sortCol, setSortCol] = useState<SortCol>('lastContact')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

    const tenantContacts: TenantContact[] = summaries
        .filter(s => s.is_active && (s.prenom || s.nom || s.name))
        .map(s => ({
            id: s.id,
            name: displayBeneficiaryName(s),
            numero_suivi: s.numero_suivi ?? null,
            lastFaceToFace: s.last_face_to_face,
            lastPhoneCall: s.last_phone_call,
            lastNote: s.last_note_date,
            tags: s.tags ?? [],
        }))

    const getLastContact = (lastFaceToFace: string | null, lastPhoneCall: string | null) =>
        [lastFaceToFace, lastPhoneCall]
            .filter((d): d is string => d !== null)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null

    const getDaysSince = (lastFaceToFace: string | null, lastPhoneCall: string | null) => {
        const last = getLastContact(lastFaceToFace, lastPhoneCall)
        if (!last) return Infinity
        return Math.floor((new Date().getTime() - new Date(last).getTime()) / (1000 * 60 * 60 * 24))
    }

    const getRowColorClass = (lastFaceToFace: string | null, lastPhoneCall: string | null) => {
        const days = getDaysSince(lastFaceToFace, lastPhoneCall)
        if (days === Infinity) return 'bg-gray-100'
        if (days <= 7) return 'bg-green-100'
        if (days <= 14) return 'bg-yellow-100'
        if (days <= 30) return 'bg-orange-100'
        return 'bg-red-100'
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    const handleToggleTag = async (beneficiaryId: string, tag: string) => {
        const current = summaries.find(s => s.id === beneficiaryId)?.tags ?? []
        const newTags = computeNewTags(current, tag)
        updateSummaryTags(beneficiaryId, newTags)
        await supabase.from('beneficiaries').update({ tags: newTags }).eq('id', beneficiaryId)
    }

    const handleSort = (col: SortCol) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        else { setSortCol(col); setSortDir('asc') }
    }

    const sortedContacts = [...tenantContacts].sort((a, b) => {
        let cmp = 0
        if (sortCol === 'name') {
            cmp = (a.name || '').localeCompare(b.name || '', 'fr')
        } else if (sortCol === 'lastContact') {
            const av = getLastContact(a.lastFaceToFace, a.lastPhoneCall)
            const bv = getLastContact(b.lastFaceToFace, b.lastPhoneCall)
            if (!av && !bv) cmp = 0
            else if (!av) cmp = 1
            else if (!bv) cmp = -1
            else cmp = new Date(av).getTime() - new Date(bv).getTime()
        } else {
            const av = a[sortCol as 'lastFaceToFace' | 'lastPhoneCall' | 'lastNote']
            const bv = b[sortCol as 'lastFaceToFace' | 'lastPhoneCall' | 'lastNote']
            if (!av && !bv) cmp = 0
            else if (!av) cmp = 1
            else if (!bv) cmp = -1
            else cmp = new Date(av).getTime() - new Date(bv).getTime()
        }
        return sortDir === 'asc' ? cmp : -cmp
    })

    const SortIcon = ({ col }: { col: SortCol }) => {
        if (sortCol !== col) return <ChevronsUpDown className="inline ml-1 h-3 w-3 text-gray-400" />
        return sortDir === 'asc'
            ? <ChevronUp className="inline ml-1 h-3 w-3 text-blue-500" />
            : <ChevronDown className="inline ml-1 h-3 w-3 text-blue-500" />
    }

    if (loading) {
        return (
            <div className={`${embedded ? 'py-12' : 'min-h-screen bg-gray-50'} flex items-center justify-center`}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des données...</p>
                </div>
            </div>
        )
    }

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
            {!embedded && <PageHeader title="Derniers contacts" icon={<Clock size={20} />} onBack={onNavigateHome} />}

            <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-gray-900">
                                Historique des contacts ({tenantContacts.length} locataires)
                            </h2>
                            <Button onClick={refresh} variant="outline" size="sm">
                                Actualiser
                            </Button>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            Couleurs :
                            <span className="inline-block w-3 h-3 bg-green-100 border mx-1"></span>≤ 7 jours
                            <span className="inline-block w-3 h-3 bg-yellow-100 border mx-1"></span>8-14 jours
                            <span className="inline-block w-3 h-3 bg-orange-100 border mx-1"></span>15-30 jours
                            <span className="inline-block w-3 h-3 bg-red-100 border mx-1"></span>&gt; 30 jours
                        </p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {([
                                        { col: 'name', label: 'Locataire' },
                                        { col: 'lastFaceToFace', label: 'Dernier face-à-face' },
                                        { col: 'lastPhoneCall', label: 'Dernier CT' },
                                        { col: 'lastNote', label: 'Dernière note' },
                                        { col: 'lastContact', label: 'Dernier contact' },
                                    ] as { col: SortCol; label: string }[]).map(({ col, label }) => (
                                        <th
                                            key={col}
                                            onClick={() => handleSort(col)}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                                        >
                                            {label}<SortIcon col={col} />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedContacts.map((tenant) => (
                                    <tr key={tenant.id} className="hover:bg-gray-50">
                                        <td className={`px-6 py-4 text-sm font-medium text-gray-900 ${getRowColorClass(tenant.lastFaceToFace, tenant.lastPhoneCall)}`}>
                                            <div className="flex items-center gap-2">
                                                {tenant.name}
                                                {onNavigateToBeneficiaire && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onNavigateToBeneficiaire(tenant.id)}
                                                        title="Voir la fiche"
                                                        className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                                    >
                                                        <ClipboardList size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(tenant.lastFaceToFace)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(tenant.lastPhoneCall)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(tenant.lastNote)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                            {formatDate(getLastContact(tenant.lastFaceToFace, tenant.lastPhoneCall))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {tenantContacts.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-500">Aucun locataire trouvé</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {!embedded && showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
}
